// Pure reducer for combat-engagement visual state. Lives in its own
// React-free module so the contract is testable under `bun test` without
// pulling in react or workspace deps.
//
// Source signal: `unit:combat` events on `useSimWorld().events`. Each event
// carries `unitId`, `defenderId`, and post-strike morale for both sides. We
// fold those into per-unit visual state so the 3D scene can apply position
// jitter (layer 1) and step-back / step-forward (layer 2) without re-reading
// the entire event log on every render.
//
// Spec: docs/02-systems/combat-visualisation.md §3, §5.

export type SimEvent = { id: number; type: string; payload?: Record<string, unknown> };

export type StepDirection = 'forward' | 'back' | null;

export interface EngagementVisualState {
  /** Tick at which the position jitter should stop being applied. */
  readonly jitterUntilTick: number;
  /** Signed pixel offset relative to the unit's base position. */
  readonly stepOffsetPx: number;
  /** Direction of the most recent step (or null if no step yet). */
  readonly stepDirection: StepDirection;
  /** Last-seen post-strike morale level — used to compute moraleDelta. */
  readonly lastMorale: number;
}

export interface CombatEngagementOptions {
  readonly jitterDurationTicks: number;
  readonly stepPx: number;
}

export const DEFAULT_COMBAT_OPTIONS: CombatEngagementOptions = {
  jitterDurationTicks: 1,
  stepPx: 8,
};

function readString(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' ? v : null;
}

function readMorale(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Pure reducer. Folds every `unit:combat` event with `id > prevLastId` into
 * per-unit visual state (events carry monotonic ids and the history is
 * capped — see bootSim). When the last id drops below the watermark (match
 * reset), the previous map is dropped entirely. Returns the new state.
 */
export function reduceCombatEngagements(
  events: ReadonlyArray<SimEvent>,
  prevLastId: number,
  prevState: ReadonlyMap<string, EngagementVisualState>,
  currentTick: number,
  options: CombatEngagementOptions = DEFAULT_COMBAT_OPTIONS,
): Map<string, EngagementVisualState> {
  const lastId = events.length > 0 ? (events[events.length - 1]!.id ?? -1) : -1;
  const firstId = events.length > 0 ? (events[0]!.id ?? 0) : -1;
  // Match reset: ids restart below the watermark → drop the previous state
  // and fold everything visible. Cap slid past the watermark (first visible
  // id jumps ahead of it) → fold everything visible too. Otherwise only
  // events strictly newer than the watermark.
  const reset = lastId < prevLastId;
  const slid = firstId > prevLastId + 1;
  const next = reset ? new Map<string, EngagementVisualState>() : new Map(prevState);
  const startId = reset || slid ? firstId : prevLastId + 1;

  const { jitterDurationTicks, stepPx } = options;
  const jitterUntilTick = currentTick + jitterDurationTicks;

  for (const ev of events) {
    if ((ev.id ?? -1) < startId) continue;
    if (ev.type !== 'unit:combat') continue;
    const payload = ev.payload;
    if (!payload) continue;
    const attackerId = readString(payload, 'unitId');
    const defenderId = readString(payload, 'defenderId');
    if (!attackerId || !defenderId) continue;
    const aMorale = readMorale(payload, 'attackerMoraleAfter');
    const dMorale = readMorale(payload, 'defenderMoraleAfter');

    if (aMorale !== null) {
      const prevAttacker = next.get(attackerId);
      const moraleDelta = prevAttacker ? aMorale - prevAttacker.lastMorale : 0;
      const stepForward = moraleDelta > 0;
      next.set(attackerId, {
        jitterUntilTick,
        stepOffsetPx: stepForward ? stepPx : (prevAttacker?.stepOffsetPx ?? 0),
        stepDirection: stepForward ? 'forward' : (prevAttacker?.stepDirection ?? null),
        lastMorale: aMorale,
      });
    } else {
      const prevAttacker = next.get(attackerId);
      next.set(attackerId, {
        jitterUntilTick,
        stepOffsetPx: prevAttacker?.stepOffsetPx ?? 0,
        stepDirection: prevAttacker?.stepDirection ?? null,
        lastMorale: prevAttacker?.lastMorale ?? 2,
      });
    }

    next.set(defenderId, {
      jitterUntilTick,
      stepOffsetPx: -stepPx,
      stepDirection: 'back',
      lastMorale: dMorale ?? next.get(defenderId)?.lastMorale ?? 2,
    });
  }

  return next;
}
